import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appEnv } from '../../../app.config';

@Injectable({
  providedIn: 'root'
})
export class SmsSendService {
  constructor(private httpClient: HttpClient) {}

  send(threadId: string, messageId: string, to: string, message: string): Observable<any> {
    return this.httpClient.post(`${appEnv.api}crm/messaging/sms/send`, {
      thread_id: threadId,
      message_id: messageId,
      to,
      message
    });
  }

  /** Starts a brand-new SMS thread (Compose's "+ New SMS") - see RingotelOutboundMessageController::startNew(). */
  composeNew(to: string, message: string): Observable<{ thread_id: string }> {
    return this.httpClient.post<{ thread_id: string }>(`${appEnv.api}crm/messaging/sms/compose`, {
      to,
      message
    });
  }

  /**
   * Bulk send (Compose's audience picker) - each recipient lands in their own
   * individual thread and shows as its own row in Message Log (not grouped -
   * each recipient's status can differ). Capped at 200 recipients per call
   * server-side (BULK_MAX_RECIPIENTS). Each recipient's actual send is
   * queued (SendCrmSmsJob, with retries) rather than sent synchronously, so
   * this resolves once the sends are queued, not once they've gone out.
   */
  sendBulk(to: string[], message: string, module: string | null = null, records: (string | null)[] | null = null): Observable<{ queued: number }> {
    return this.httpClient.post<{ queued: number }>(`${appEnv.api}crm/messaging/sms/send-bulk`, {
      to,
      message,
      module,
      // records[i] = to[i]'s CRM record - its {{first_name}} etc. fill that recipient's SMS.
      ...(records ? { records } : {})
    });
  }

  /** Deterministic thread lookup for a phone number - see
   *  RingotelOutboundMessageController::threadForNumber(). Lets a CRM
   *  record's SMS tab find (or implicitly create, on first send) the same
   *  thread real inbound replies from that number land in. thread_ids also
   *  lists the ids older sends stored this number's history under (04... vs
   *  61... formats), canonical first. */
  threadForNumber(number: string): Observable<{ thread_id: string; thread_ids?: string[]; to: string }> {
    return this.httpClient.get<{ thread_id: string; thread_ids?: string[]; to: string }>(`${appEnv.api}crm/messaging/sms/thread-for-number`, {
      params: { number }
    });
  }

  /** Clears unreadCount on an SMS thread and records who read it - on the
   *  thread and on each of messageIds (the inbound messages just shown). */
  markRead(threadId: string, messageIds: string[] = []): Observable<any> {
    return this.httpClient.post(`${appEnv.api}crm/messaging/sms/mark-read`, { thread_id: threadId, message_ids: messageIds });
  }

  /** Flags an SMS thread unread again (messageIds = the inbound messages to
   *  flag) - see RingotelOutboundMessageController::markUnread(). */
  markUnread(threadId: string, messageIds: string[]): Observable<any> {
    return this.httpClient.post(`${appEnv.api}crm/messaging/sms/mark-unread`, { thread_id: threadId, message_ids: messageIds });
  }

  /** Deletes the thread and its messages - see RingotelOutboundMessageController::destroy(). */
  delete(threadId: string): Observable<any> {
    return this.httpClient.delete(`${appEnv.api}crm/messaging/sms/${threadId}`);
  }

  /** Ringotel delivery status for one sent message - see RingotelOutboundMessageController::status(). */
  status(providerMessageId: string): Observable<any> {
    return this.httpClient.get(`${appEnv.api}crm/messaging/sms/status`, { params: { id: providerMessageId } });
  }
}
